/**
 * Apply the Generic Report Template as the printable Note Report shell.
 *
 * Clinical body text is supplied by the existing note editor / note-generation
 * path and is never redesigned here. Missing template or resolver failure
 * leaves the caller on the previous Print Note layout.
 */
import { format, isValid } from "date-fns";
import { resolveReportHtml } from "@/lib/genericReportPlaceholders";
import { looksLikeNoteRtf, paginateNoteRtfBody } from "@/lib/noteRtf";
import {
  NA,
  encounterDate,
  encounterLocation,
  encounterOrganization,
  encounterProvider,
  formatAddress,
  formatDate,
} from "@/lib/patientChart";
import { providerDisplay } from "@/lib/appointmentApi";

/** US Letter at 96 CSS px/in, matching @page size: letter; margin: 0.6in. */
export const LETTER_PAGE_HEIGHT_IN = 11;
export const PRINT_PAGE_MARGIN_IN = 0.6;
export const PRINT_PAGE_INNER_HEIGHT_IN = LETTER_PAGE_HEIGHT_IN - 2 * PRINT_PAGE_MARGIN_IN;
export const CSS_PX_PER_IN = 96;
export const BODY_FONT_SIZE_PX = 13;
export const BODY_LINE_HEIGHT = 1.55;
/** Matches .note-print-body--rtf p/div { margin: 0 0 0.35em }. */
export const BODY_BLOCK_MARGIN_EM = 0.35;
/** One wrapped print line. RTF block margins are charged once per block. */
export const BODY_LINE_PX = BODY_FONT_SIZE_PX * BODY_LINE_HEIGHT;
export const BODY_BLOCK_MARGIN_LINES = BODY_BLOCK_MARGIN_EM / BODY_LINE_HEIGHT;

/**
 * Reserved header/footer bands measured from the accepted print shell.
 * Keep a small slack so a full body chunk cannot collide with the footer.
 */
export const FIRST_PAGE_HEADER_RESERVE_IN = 2.15;
export const SUBSEQUENT_HEADER_RESERVE_IN = 0.40;
export const FOOTER_RESERVE_IN = 0.50;
export const PRINT_PAGE_FIT_SLACK_IN = 0.06;

export const NOTE_BODY_WRAP_COLUMNS = 92;

export function availableBodyLines(headerReserveIn, footerReserveIn = FOOTER_RESERVE_IN) {
  const bodyIn =
    PRINT_PAGE_INNER_HEIGHT_IN - headerReserveIn - footerReserveIn - PRINT_PAGE_FIT_SLACK_IN;
  const bodyPx = bodyIn * CSS_PX_PER_IN;
  return Math.max(1, Math.floor(bodyPx / BODY_LINE_PX));
}

export const FIRST_PAGE_BODY_LINES = availableBodyLines(FIRST_PAGE_HEADER_RESERVE_IN);
export const NEXT_PAGE_BODY_LINES = availableBodyLines(SUBSEQUENT_HEADER_RESERVE_IN);

export function reportText(value) {
  if (value == null || value === "" || value === NA || value === "—") return "";
  return String(value);
}

export function reportDate(iso) {
  if (!iso) return "";
  const formatted = formatDate(iso);
  return formatted === NA ? "" : formatted;
}

export function resourceRefId(value) {
  if (!value) return "";
  if (typeof value === "object") {
    return resourceRefId(value.reference || value.id || "");
  }
  const text = String(value);
  if (text.includes("/")) return text.split("/").pop() || "";
  return text;
}

export function firstLocationId(encounter) {
  if (encounter?.locationIds?.[0]) return String(encounter.locationIds[0]);
  return resourceRefId(encounter?.location?.[0]?.location);
}

export function firstOrganizationId({ encounter, patient, location } = {}) {
  return (
    resourceRefId(location?.managingOrganization) ||
    reportText(encounter?.serviceProviderId) ||
    resourceRefId(encounter?.serviceProvider) ||
    resourceRefId(patient?.managingOrganization)
  );
}

export function telecomValue(resource, system) {
  const list = resource?.telecom || [];
  const match = list.find((item) => item?.system === system);
  return reportText(match?.value);
}

export function formatResourceAddress(resource) {
  if (!resource) return "";
  const raw = resource.address;
  const asPatientShape = Array.isArray(raw) ? resource : { address: raw ? [raw] : [] };
  const formatted = formatAddress(asPatientShape);
  return formatted === NA ? "" : formatted;
}

function pickPractice({ organization, location, encounter } = {}) {
  const orgName = reportText(organization?.name);
  const fromEncounter = reportText(encounterOrganization(encounter));
  const locName = reportText(location?.name);
  return {
    name: orgName || fromEncounter || locName,
    address: formatResourceAddress(organization) || formatResourceAddress(location),
    phone: telecomValue(organization, "phone") || telecomValue(location, "phone"),
    fax: telecomValue(organization, "fax") || telecomValue(location, "fax"),
  };
}

export function splitProviderNameAndTitle(displayName, title) {
  const name = reportText(displayName);
  const cred = reportText(title);
  if (!name) return { name: "", title: cred };
  if (!cred) return { name, title: "" };
  const escaped = cred.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const trailing = new RegExp(`(,\\s*|\\s+)${escaped}\\s*$`, "i");
  if (trailing.test(name)) {
    return { name: name.replace(trailing, "").trim(), title: cred };
  }
  return { name, title: cred };
}

function pickProvider({ providerUser, appointment, encounter } = {}) {
  if (providerUser) {
    return splitProviderNameAndTitle(providerUser.displayName, providerUser.title);
  }
  return splitProviderNameAndTitle(
    reportText(providerDisplay(appointment)) || reportText(encounterProvider(encounter)),
    "",
  );
}

export function logoHtmlFromSrc(src) {
  const safe = reportText(src);
  if (!safe) return "";
  return `<img class="grd-logo" alt="Practice logo" src="${safe}" />`;
}

export function isUsableGenericReportTemplate(template) {
  if (!template || typeof template !== "object") return false;
  return ["firstPageHeaderHtml", "subsequentHeaderHtml", "footerHtml"].some(
    (key) => typeof template[key] === "string",
  );
}

export function buildNoteReportPlaceholderValues({
  fields,
  encounter,
  note,
  organization,
  location,
  appointment,
  providerUser,
  now = new Date(),
  pageNumber = 1,
  logoHtml = "",
} = {}) {
  const practice = pickPractice({ organization, location, encounter });
  const provider = pickProvider({ providerUser, appointment, encounter });
  const printDate = now instanceof Date && isValid(now) ? format(now, "MM/dd/yyyy") : reportDate(now);
  return {
    practicename: practice.name,
    practiceaddress: practice.address,
    practicephone: practice.phone,
    practicefax: practice.fax,
    practicelogo: logoHtml || "",
    fullname: reportText(fields?.name),
    dob: reportDate(fields?.birthDate),
    mrn: reportText(fields?.mrn),
    dateofservice: reportDate(encounterDate(encounter)),
    provider: provider.name,
    providertitle: provider.title,
    todaysdate: printDate,
    pagenumber: String(pageNumber),
    notetype: reportText(note?.noteType?.displayName),
    locationname: reportText(location?.name) || reportText(encounterLocation(encounter)),
    locationaddress: formatResourceAddress(location),
    locationphone: telecomValue(location, "phone"),
    locationfax: telecomValue(location, "fax"),
  };
}

export function wrapPlainTextLines(text, columns = NOTE_BODY_WRAP_COLUMNS) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const wrapped = [];
  for (const line of lines) {
    if (line.length <= columns) {
      wrapped.push(line);
      continue;
    }
    let rest = line;
    while (rest.length > columns) {
      let cut = rest.lastIndexOf(" ", columns);
      if (cut < Math.floor(columns * 0.5)) cut = columns;
      wrapped.push(rest.slice(0, cut));
      rest = rest.slice(cut).replace(/^\s+/, "");
    }
    if (rest.length) wrapped.push(rest);
  }
  return wrapped;
}

export function paginateNoteBody(
  text,
  {
    firstPageLines = FIRST_PAGE_BODY_LINES,
    nextPageLines = NEXT_PAGE_BODY_LINES,
    columns = NOTE_BODY_WRAP_COLUMNS,
  } = {},
) {
  if (looksLikeNoteRtf(text)) {
    return paginateNoteRtfBody(text, {
      firstPageLines,
      nextPageLines,
      columns,
      wrapLines: wrapPlainTextLines,
      blockExtraLines: BODY_BLOCK_MARGIN_LINES,
    });
  }
  const lines = wrapPlainTextLines(text, columns);
  if (!lines.length) return [""];
  const pages = [];
  let index = 0;
  let size = firstPageLines;
  while (index < lines.length) {
    pages.push(lines.slice(index, index + size).join("\n"));
    index += size;
    size = nextPageLines;
  }
  return pages.length ? pages : [""];
}

export function composeTemplatedNotePages({ template, values, content } = {}) {
  if (!isUsableGenericReportTemplate(template)) return null;
  const chunks = paginateNoteBody(content);
  return chunks.map((body, index) => {
    const pageNumber = index + 1;
    const pageValues = { ...values, pagenumber: String(pageNumber) };
    const headerSource =
      pageNumber === 1 ? template.firstPageHeaderHtml : template.subsequentHeaderHtml;
    return {
      pageNumber,
      headerHtml: resolveReportHtml(headerSource || "", pageValues),
      footerHtml: resolveReportHtml(template.footerHtml || "", pageValues),
      content: body,
    };
  });
}

export function applyGenericReportTemplate({
  template,
  fields,
  encounter,
  note,
  content,
  reportContext = {},
} = {}) {
  if (!isUsableGenericReportTemplate(template)) return null;
  try {
    const values = buildNoteReportPlaceholderValues({
      fields,
      encounter,
      note,
      organization: reportContext.organization,
      location: reportContext.location,
      appointment: reportContext.appointment,
      providerUser: reportContext.providerUser,
      now: reportContext.now,
      logoHtml: reportContext.logoHtml || "",
    });
    const pages = composeTemplatedNotePages({ template, values, content });
    if (!pages?.length) return null;
    return {
      templateId: template.id || "generic",
      versionId: template.versionId,
      values,
      pages,
    };
  } catch {
    return null;
  }
}
